// SceneIframe.js
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MozillaHubRoomsApi, DidacticRoutesAPI } from "../../../../api";
import "./SceneIframe.scss";

const mozillaHubRoomsApi = new MozillaHubRoomsApi();
const didacticRoutesAPI = new DidacticRoutesAPI();

export function SceneIframe({
  accessToken,
  shortLink,
  fallbackUrl,
  routeId,
  scene, // se establece al pulsar Entrar
  onResolvedUrl, // callback opcional
}) {
  const { t } = useTranslation("evaluations");
  const [iframeUrl, setIframeUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Mantener una referencia estable al callback (no en deps)
  const resolvedCbRef = useRef(onResolvedUrl);
  useEffect(() => {
    resolvedCbRef.current = onResolvedUrl;
  }, [onResolvedUrl]);

  useEffect(() => {
    if (!scene || !scene._id) return;

    let cancelled = false;

    const safeSet =
      (fn) =>
      (...args) => {
        if (!cancelled) fn(...args);
      };
    const setLoadingSafe = safeSet(setLoading);
    const setErrorSafe = safeSet(setError);
    const setUrlSafe = safeSet(setIframeUrl);

    const notifyResolved = (url) => {
      if (resolvedCbRef.current) {
        try {
          resolvedCbRef.current(url || "");
        } catch (_e) {}
      }
    };

    const loadIframe = async () => {
      setLoadingSafe(true);
      setErrorSafe(null);

      try {
        const sceneUrl = scene.urlsala || "";
        const isSceneMaker = sceneUrl.includes("/scenes/");

        // Si NO es SceneMaker → abrir nueva pestaña y no embeber.
        // No pasa por /roomshub/load: anotamos la apertura con la baliza.
        if (!isSceneMaker) {
          mozillaHubRoomsApi.trackRoomOpen(accessToken, {
            sceneRefId: scene._id,
            url: sceneUrl,
            title: scene.title,
            source: "route_launch",
          });
          window.open(sceneUrl, "_blank", "noopener,noreferrer");
          notifyResolved(sceneUrl);
          setUrlSafe(null);

          // ✅ Persistir escena activa también para URLs externas
          if (routeId) {
            await didacticRoutesAPI.updateDidacticRoute(accessToken, routeId, {
              sceneactive: sceneUrl,
              sceneactive_Id: scene._id,
            });
          }

          return;
        }

        // Sin shortLink → usar fallback o la propia URL de la escena
        if (!shortLink) {
          const urlToUse = fallbackUrl || sceneUrl;
          mozillaHubRoomsApi.trackRoomOpen(accessToken, {
            sceneRefId: scene._id,
            url: urlToUse,
            title: scene.title,
            source: "route_launch",
          });
          setUrlSafe(urlToUse);
          notifyResolved(urlToUse);

          if (routeId) {
            await didacticRoutesAPI.updateDidacticRoute(accessToken, routeId, {
              sceneactive: urlToUse,
              sceneactive_Id: scene._id,
            });
          }
          return;
        }

        // Con itinerario -> el servidor da la sala activa de la clase: reutiliza
        // la que ya tienen los alumnos si sigue viva (antes cada "Entrar" creaba
        // otra y la clase quedaba repartida) o crea una nueva y la guarda.
        // Tambien registra la apertura (route_launch). Si falla, flujo antiguo.
        if (routeId) {
          try {
            const active = await didacticRoutesAPI.enterActiveScene(
              accessToken,
              routeId,
              scene._id,
            );
            setUrlSafe(active.url);
            notifyResolved(active.url);
            return;
          } catch (e) {
            console.warn(
              "enterActiveScene fallo, se crea la sala por /roomshub/load:",
              e?.message,
            );
          }
        }

        // Con shortLink → crear/abrir sala embebida. `source: route_launch`
        // marca la apertura como USO en clase (no ojeo de catalogo).
        const result = await mozillaHubRoomsApi.LoadRoom(
          accessToken,
          shortLink,
          { source: "route_launch", sceneRefId: scene._id },
        );
        const finalUrl = `https://educa360.es/${result.room.id}?embed_token=${result.room.embedToken}`;
        setUrlSafe(finalUrl);
        notifyResolved(finalUrl);

        if (routeId) {
          await didacticRoutesAPI.updateDidacticRoute(accessToken, routeId, {
            sceneactive: finalUrl,
            sceneactive_Id: scene._id,
          });
        }
      } catch (err) {
        setErrorSafe(err?.message || "Error desconocido");
      } finally {
        setLoadingSafe(false);
      }
    };

    loadIframe();
    return () => {
      cancelled = true;
    };
    // 👇 Importante: NO incluir onResolvedUrl (está estabilizado vía ref)
  }, [scene, accessToken, shortLink, fallbackUrl, routeId]);

  if (loading) {
    return (
      <div
        className="evaluacion-iframe"
        style={{ width: "100%", height: "100%" }}
      >
        {t("sceneIframe.loading")}
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="evaluacion-iframe"
        style={{ width: "100%", height: "100%" }}
      >
        {t("sceneIframe.error")}: {error}
      </div>
    );
  }

  return (
    <div
      className="evaluacion-iframe"
      style={{ width: "100%", height: "100%" }}
    >
      {iframeUrl && (
        <iframe
          src={iframeUrl}
          className="evaluacion-iframe"
          title={t("sceneIframe.iframeTitle")}
          allow="microphone; camera; vr; xr-spatial-tracking; speaker;"
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            display: "block",
          }}
        />
      )}
    </div>
  );
}
